import './App.css'
import { BrowserRouter, Route, Routes, useLocation } from 'react-router'
import Home from './pages/Home'
import Booking from './pages/Booking'
import Rent from './pages/Rent'
import RentConfirmation from './pages/RentConfirmation'
import Confirmation from './pages/Confirmation'
import AboutUs from './pages/AboutUs';
import React, { Dispatch, useEffect, useMemo, useState } from 'react';
import { Project } from './types/project.type';
import { useGetProject } from './hooks/useGetProject';
import { User } from './types/user.type';
import { Service, ServiceType } from './types/service.type';
import { Product, ProductCategory } from './types/product.type';
import InvoicePage from './pages/InvoicePage';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { toAbsoluteHttpUrl } from './utils/absoluteUrl';
import { resolveOptionalColor } from './utils/buttonColor';
import i18n, { changeAppLanguage, localeFromCountry, readStoredLocale } from './i18n';

export const ProjectContext = React.createContext<{
  project: Project | undefined;
  setProject: Dispatch<Project>
}>({
  project: undefined,
  setProject: (project: Project) => { }
})

export const DataContext = React.createContext<{
  customer: User | undefined;
  setCustomer: Dispatch<User>
  services: Service[]
  setServices: Dispatch<Service[]>
  serviceTypes: ServiceType[]
  setServiceTypes: Dispatch<ServiceType[]>
  products: Product[]
  setProducts: Dispatch<Product[]>
  productCategories: ProductCategory[]
  setProductCategories: Dispatch<ProductCategory[]>
  selectedServices: Service[]
  setSelectedServices: Dispatch<Service[]>
}>({
  customer: undefined,
  setCustomer: (customer: User) => { },
  services: [],
  setServices: (services: Service[]) => { },
  serviceTypes: [],
  setServiceTypes: (serviceTypes: ServiceType[]) => { },
  products: [],
  setProducts: (products: Product[]) => { },
  productCategories: [],
  setProductCategories: (productCategories: ProductCategory[]) => { },
  selectedServices: [],
  setSelectedServices: (selectedServices: Service[]) => { },
})

function App() {
  const [project, setProject] = useState<Project | undefined>(undefined)
  const [customer, setCustomer] = useState<User | undefined>(undefined)
  const [services, setServices] = useState<Service[]>([])
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [productCategories, setProductCategories] = useState<ProductCategory[]>([])
  const [selectedServices, setSelectedServices] = useState<Service[]>([])

  const { project: projectData } = useGetProject()

  useEffect(() => {
    if (project) {
      const logoImage = project.logoImageFiles?.[0]?.s3Url || ''

      const manifestLink = document.querySelector("link[rel='manifest']") as HTMLLinkElement
      if (manifestLink) {
        const manifestUrl = manifestLink.href
        fetch(manifestUrl)
          .then(response => response.json())
          .then(manifest => {
            if (logoImage) {
              manifest.icons = [
                {
                  src: logoImage,
                  sizes: "192x192",
                  type: "image/png"
                },
                {
                  src: logoImage,
                  sizes: "512x512",
                  type: "image/png"
                }
              ]
              manifest.name = project.name
              manifest.short_name = project.name
              manifest.description = project.welcomeMessage
              manifest.start_url = window.location.href
              manifest.display = "standalone"
              manifest.theme_color = resolveOptionalColor(project.themeColor) ?? "#000000"
              manifest.background_color = resolveOptionalColor(project.serviceColor) ?? "#ffffff"
              const blob = new Blob([JSON.stringify(manifest)], { type: 'application/json' })
              const newManifestUrl = URL.createObjectURL(blob)
              manifestLink.href = newManifestUrl
            }
          })
          .catch(error => console.error('Error updating manifest:', error))
      }


    }
  }, [project])

  useEffect(() => {
    if (projectData) {
      setProject(projectData)
    }
  }, [projectData])

  // When the user has no saved preference, follow the project country once it loads.
  useEffect(() => {
    if (!projectData || readStoredLocale()) return
    const locale = localeFromCountry(projectData.country)
    if (i18n.language !== locale) {
      void changeAppLanguage(locale)
    }
  }, [projectData])

  return (
    <HelmetProvider>
      <I18nextProvider i18n={i18n}>
        <ProjectContext.Provider value={{ project: project, setProject: setProject }}>
          <DataContext.Provider value={{
            customer: customer,
            setCustomer: setCustomer,
            services: services,
            setServices: setServices,
            serviceTypes: serviceTypes,
            setServiceTypes: setServiceTypes,
            products: products,
            setProducts: setProducts,
            productCategories: productCategories,
            setProductCategories: setProductCategories,
            selectedServices: selectedServices,
            setSelectedServices: setSelectedServices,
          }}>
            <BrowserRouter>
              <SEO />
              <AnimatedRoutes />
            </BrowserRouter>
          </DataContext.Provider>
        </ProjectContext.Provider>
      </I18nextProvider>
    </HelmetProvider>
  );
}

function SEO() {
  const { project } = React.useContext(ProjectContext)
  const location = useLocation()
  const { i18n } = useTranslation()

  const ogImage = useMemo(
    () =>
      toAbsoluteHttpUrl(
        project?.coverImageFiles?.[0]?.s3Url || project?.logoImageFiles?.[0]?.s3Url
      ),
    [project]
  )

  const ogDescription =
    project?.welcomeMessage?.trim() ||
    project?.motto?.trim() ||
    project?.description?.trim() ||
    ''

  const title = project?.name ?? 'Booking'

  const canonicalUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${location.pathname}`
      : ''

  const htmlLang = i18n.language?.startsWith('id') ? 'id' : 'en'
  const ogLocale = htmlLang === 'id' ? 'id_ID' : 'en_US'

  const structuredData = useMemo(() => {
    if (!project) return null
    const data: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: project.name,
      description: ogDescription || undefined,
      image: ogImage || undefined,
      url: canonicalUrl || undefined,
      email: project.contactEmail || project.email || undefined,
      telephone: project.contactNo || undefined,
    }
    if (project.address) {
      data.address = {
        '@type': 'PostalAddress',
        streetAddress: project.address,
        addressCountry: project.country || undefined,
      }
    }
    Object.keys(data).forEach((k) => data[k] === undefined && delete data[k])
    return data
  }, [project, ogDescription, ogImage, canonicalUrl])

  return (
    <Helmet
      key={project?.projectId ?? 'loading'}
      htmlAttributes={{ lang: htmlLang }}
    >
      <title>{title}</title>
      {ogDescription ? <meta name="description" content={ogDescription} /> : null}

      <meta property="og:type" content="website" />
      <meta property="og:locale" content={ogLocale} />
      {canonicalUrl ? <meta property="og:url" content={canonicalUrl} /> : null}
      {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}
      {project?.name ? <meta property="og:site_name" content={project.name} /> : null}
      <meta property="og:title" content={title} />
      {ogDescription ? <meta property="og:description" content={ogDescription} /> : null}
      {ogImage ? <meta property="og:image" content={ogImage} /> : null}
      {ogImage && ogImage.startsWith('https:') ? (
        <meta property="og:image:secure_url" content={ogImage} />
      ) : null}
      {ogImage && project?.name ? (
        <meta property="og:image:alt" content={project.name} />
      ) : null}

      <meta name="twitter:card" content="summary_large_image" />
      {canonicalUrl ? <meta name="twitter:url" content={canonicalUrl} /> : null}
      {typeof window !== 'undefined' ? (
        <meta name="twitter:domain" content={window.location.hostname} />
      ) : null}
      <meta name="twitter:title" content={title} />
      {ogDescription ? <meta name="twitter:description" content={ogDescription} /> : null}
      {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}
      {ogImage && project?.name ? (
        <meta name="twitter:image:alt" content={project.name} />
      ) : null}

      {project?.logoImageFiles?.[0]?.s3Url ? (
        <link
          rel="apple-touch-icon"
          href={toAbsoluteHttpUrl(project.logoImageFiles[0].s3Url) ?? ''}
        />
      ) : null}
      {project?.logoImageFiles?.[0]?.s3Url ? (
        <link rel="icon" href={toAbsoluteHttpUrl(project.logoImageFiles[0].s3Url) ?? ''} />
      ) : null}
      {resolveOptionalColor(project?.themeColor) ? (
        <meta name="theme-color" content={resolveOptionalColor(project?.themeColor)} />
      ) : null}

      {structuredData ? (
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      ) : null}
    </Helmet>
  )
}

function AnimatedRoutes() {
  const location = useLocation()

  return (
    <Routes location={location}>
      <Route path="/" element={<Home />} />
      <Route path="/booking" element={<Booking />} />
      <Route path="/rent" element={<Rent />} />
      <Route path="/rent/confirmation" element={<RentConfirmation />} />
      <Route path="/about-us" element={<AboutUs />} />
      <Route path="/confirmation" element={<Confirmation />} />
      <Route path="/invoice/:shortUrlCode" element={<InvoicePage />} />
    </Routes>
  )
}

export default App
